/* eslint-disable no-control-regex */
/**
 * @file 検証処理ライブラリ
 * @namespace rahmen
 */

/**
 * @module validators
 */

import core from '@/rahmen/core'
import dayjs from 'dayjs'
import message from '@/ddv2.library/messages'

/**
 * @see 【サンプル】引数のObject型定義
 * @example
const paramStruct = {
  value: 'target' || 0 || new Date(),
  length: 0,
  intDigit: 0, // Numericの場合のみ
  decimalDigit: 0, // Numericの場合のみ
  max: 0 || 'YYYY/MM/DD',
  min: 0 || 'YYYY/MM/DD',
  isStraddle: false // Timeの場合のみ
}
 */
const paramStruct = {
  value: 'target' || 0 || new Date(),
  length: 0,
  intDigit: 0, // Numericの場合のみ
  decimalDigit: 0, // Numericの場合のみ
  max: 0 || 'YYYY/MM/DD',
  min: 0 || 'YYYY/MM/DD',
  isStraddle: false // Timeの場合のみ
}

/**
 * 必須検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0001
 */
function requireValidate ({ value }) {
  if (value === null || value === undefined) { return message.get('COMV0001') }

  const v = '' + value
  if (v.trim() === '') { return message.get('COMV0001') }
}

/**
 * 必須検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0001
 */
function requireMultiValidate ({ value }) {
  if (value === null || value === undefined) { return message.get('COMV0001') }
  console.log('requireMultiValidate', value)
  if (value.length === 0) { return message.get('COMV0001') }
}

/**
 * 文字列入力長検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Object} obj.length 検証対象検証桁数
 * @returns COMV0053
 */
function lengthValidate ({ value, length }) {
  if (value === null || value === undefined) { return }
  if (length === null || length === undefined) { return }
  const v = '' + value
  return v.trim().length <= length ? undefined : message.get('COMV0054', [null, length])
}

/**
 * 文字列入力長一致検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Object} obj.length 検証対象検証桁数
 * @returns COMV0053
 */
function fixedLengthValidate ({ value, length }) {
  if (value === '' || value === null || value === undefined) { return }
  if (length === null || length === undefined) { return }
  const v = '' + value
  return v.trim().length === length ? undefined : message.get('COMV0057', [null, length])
}

/**
 * 半角カナ検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0006
 */
function halfWidthKanaValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[ｦ-ﾟ\u{20}]*$/)
  return reg.test(value) ? undefined : message.get('COMV0006')
}

/**
 * 全角カナ検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0011
 */
function fullWidthKanaValidate ({ value }) {
  // 業務仕様として許容させるためにすべてtrueとする
  return undefined
  /*
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[ァ-ヶー\u{3000}]*$/)
  return reg.test(value) ? undefined : message.get('COMV0011')
  */
}

/**
 * 数字検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0004
 */
function numberValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[0-9]*$/)
  return reg.test(value) ? undefined : message.get('COMV0004')
}

/**
 * 全角検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0002
 */
function fullAngleValidate ({ value }) {
  // 業務仕様として許容させるためにすべてtrueとする
  return undefined
  /*
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[^\x01-\x7E\uFF61-\uFF9F]*$/)
  return reg.test(value) ? undefined : message.get('COMV0002')
  */
}

/**
 * 半角検証(半角カナ＋半角英数記号)
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0003
 */
function halfAngleValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[ｦ-ﾟ\u{20} a-zA-Z0-9 !@#$%^&*()_+--=[\]{};:?,.~/]*$/)
  return reg.test(value) ? undefined : message.get('COMV0003')
}

/**
 * ひらがな検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0017
 */
function hiraganaValidate ({ value }) {
  if (value === null || value === undefined) { return }
  // eslint-disable-next-line no-irregular-whitespace
  const reg = new RegExp(/^[ぁ-んー　]*$/)
  return reg.test(value) ? undefined : message.get('COMV0017')
}

/**
 * 半角英数検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0007
 */
function alphanumericValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[0-9a-zA-Z ]*$/)
  return reg.test(value) ? undefined : message.get('COMV0007')
}

/**
 * 半角英数(記号許可)検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0008
 */
function alphanumericWithSymbolValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const reg = new RegExp(/^[a-zA-Z0-9 !@#$%^&*()_+--=[\]{};:?,.~/]*$/)
  return reg.test(value) ? undefined : message.get('COMV0008')
}

/**
 * 数値型検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0004
 */
function numericalValidate ({ value }) {
  if (value === null || value === undefined) { return }
  const v = '' + value
  if (v === '') { return }
  return core.isNumeric(v) ? undefined : message.get('COMV0004')
}

/**
 * 数値桁数検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Number} obj.intDigit 検証対象正数部桁数
 * @param {Number} obj.decimalDigit 検証対象少数部桁数
 * @returns [COMV0004, COMV0053, COMV0056]
 */
function numericalDigitValidate ({ value, intDigit, decimalDigit }) {
  if (value === null || value === undefined) { return }
  if (intDigit === null || intDigit === undefined) { return }
  if (decimalDigit === null || decimalDigit === undefined) { return }
  if (numericalValidate({ value }) !== undefined) { return message.get('COMV0004') }

  let val = String(value).replace('-', '')
  let isInt = val.indexOf('.') === -1

  const num = isInt ? parseInt(val, 10) : parseFloat(val, 10)
  val = String(num)
  isInt = val.indexOf('.') === -1

  console.log(value, val, intDigit, decimalDigit)

  if (isInt && val.length > intDigit) { return message.get('COMV0053', [null, intDigit]) }
  if (!isInt) {
    const intLen = val.split('.')[0].length
    const decLen = val.split('.')[1].length
    if (intLen > intDigit || decLen > decimalDigit) { return message.get('COMV0056', [null, intDigit, decimalDigit]) }
  }
  return undefined
}

/**
 * 数値桁数一致検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Number} obj.intDigit 検証対象正数部桁数
 * @param {Number} obj.decimalDigit 検証対象少数部桁数
 * @returns [COMV0004, COMV0053, COMV0056]
 */
function fixedNumericalDigitValidate ({ value, intDigit, decimalDigit }) {
  if (value === null || value === undefined) { return }
  if (intDigit === null || intDigit === undefined) { return }
  if (decimalDigit === null || decimalDigit === undefined) { return }
  if (numericalValidate({ value }) !== undefined) { return message.get('COMV0004') }

  let val = String(value).replace('-', '')
  let isInt = val.indexOf('.') === -1

  const num = isInt ? parseInt(val, 10) : parseFloat(val, 10)
  val = String(num)
  isInt = val.indexOf('.') === -1

  console.log(value, val, intDigit, decimalDigit)

  if (isInt && val.length === intDigit) { return message.get('COMV0051', [null, intDigit]) }
  if (!isInt) {
    const intLen = val.split('.')[0].length
    const decLen = val.split('.')[1].length
    if (intLen === intDigit || decLen === decimalDigit) { return message.get('COMV0056', [null, intDigit, decimalDigit]) }
  }
  return undefined
}

/**
 * 数値範囲検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Number} obj.max 検証対象許容最大値
 * @param {Number} obj.min 検証対象許容最小値
 * @returns [COMV0004, COMV0114]
 */
function numericalRangeValidate ({ value, max, min }) {
  if (value === null || value === undefined) { return }
  if (max === null || max === undefined) { return }
  if (min === null || min === undefined) { return }
  if (numericalValidate({ value }) !== undefined) { return message.get('COMV0004') }

  const val = parseFloat(value, 10)
  return (val >= min && val <= max) ? undefined : message.get('COMV0114', [null, min, max])
}

/**
 * 日付型検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0016
 */
function dateValidate ({ value }) {
  if (value === null || value === undefined || value === '') { return }

  var isValid = dayjs(value, 'YYYY/MM/DD', true).isValid()
  if (isValid) { isValid = (value === dayjs(value, 'YYYY/MM/DD', true).format('YYYY/MM/DD')) }

  return isValid ? undefined : message.get('COMV0016')
}

/**
 * 日付型範囲検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {String} obj.max 検証対象許容最大値
 * @param {String} obj.min 検証対象許容最小値
 * @returns [COMV0106, COMV0107, COMV0120]
 */
function dateRangeValidate ({ value, max, min }) {
  if (value === null || value === undefined || value === '') { return }

  const hasMax = !(max === null || max === undefined || max === '')
  const hasMin = !(min === null || min === undefined || min === '')

  if (!hasMax && !hasMin) { return }

  const target = dayjs(value, 'YYYY/MM/DD', true).unix()
  const maxDt = dayjs(max, 'YYYY/MM/DD', true).unix()
  const minDt = dayjs(min, 'YYYY/MM/DD', true).unix()

  console.log(value, max, min, target, maxDt, minDt)

  if (hasMax && hasMin) {
    return (target <= maxDt && minDt <= target) ? undefined : message.get('COMV0120', [null, min, max])
  } else if (hasMax) {
    return (target <= maxDt) ? undefined : message.get('COMV0107', [null, max])
  } else if (hasMin) {
    return (minDt <= target) ? undefined : message.get('COMV0106', [null, min])
  }
}

/**
 * 年月検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns COMV0012
 */
function yearMonthValidate ({ value }) {
  if (value === null || value === undefined || value === '') { return }

  var isValid = dayjs(`${value}/01`, 'YYYY/MM/DD', true).isValid()
  if (isValid) { isValid = (`${value}/01` === dayjs(`${value}/01`, 'YYYY/MM/DD', true).format('YYYY/MM/DD')) }

  return isValid ? undefined : message.get('COMV0012')
}

/**
 * 年月型範囲検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {String} obj.max 検証対象許容最大値
 * @param {String} obj.min 検証対象許容最小値
 * @returns [COMV0106, COMV0107, COMV0120]
 */
function yearMonthRangeValidate ({ value, max, min }) {
  if (value === null || value === undefined || value === '') { return }

  const hasMax = !(max === null || max === undefined || max === '')
  const hasMin = !(min === null || min === undefined || min === '')

  if (!hasMax && !hasMin) { return }

  const target = dayjs(`${value}/01`, 'YYYY/MM/DD', true).unix()
  const maxDt = dayjs(`${max}/01`, 'YYYY/MM/DD', true).unix()
  const minDt = dayjs(`${min}/01`, 'YYYY/MM/DD', true).unix()

  console.log(value, max, min, target, maxDt, minDt)

  if (hasMax && hasMin) {
    return (target <= maxDt && minDt <= target) ? undefined : message.get('COMV0120', [null, min, max])
  } else if (hasMax) {
    return (target <= maxDt) ? undefined : message.get('COMV0107', [null, max])
  } else if (hasMin) {
    return (minDt <= target) ? undefined : message.get('COMV0106', [null, min])
  }
}

/**
 * 時刻検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @param {Boolean} obj.isStraddle 検証対象日跨ぎ有無
 * @returns [COMV0014, COMV0013, COMV0018]
 */
function timeValidate ({ value, isStraddle }) {
  if (value === null || value === undefined || value === '') { return }
  const cnt = (value.match(new RegExp(':', 'g')) || []).length
  if (cnt !== 1) { return message.get('COMV0014') }

  const upperHour = isStraddle ? 99 : 23
  const part = value.split(':')
  const h = parseInt(part[0], 10)
  const m = parseInt(part[1], 10)

  if (h >= 0 && h <= upperHour && m >= 0 && m <= 59) { return }
  return isStraddle ? message.get('COMV0018') : message.get('COMV0013')
}

/**
 * 時間検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 * @returns [COMV0014, COMV0018]
 */
function timeSpanValidate ({ value }) {
  if (value === null || value === undefined || value === '') { return }
  const cnt = (value.match(new RegExp(':', 'g')) || []).length
  if (cnt !== 1) { return message.get('COMV0014') }

  const part = value.split(':')
  const h = parseInt(part[0], 10)
  const m = parseInt(part[1], 10)

  if (h >= 0 && h <= 999 && m >= 0 && m <= 59) { return }
  return message.get('COMV0019')
}

/**
 * 郵便番号検証
 * @param {Object} obj 検証対象
 * @param {Object} obj.value 検証対象値
 */
function postCodeValidate ({ value }) {
  if (value === null || value === undefined || value === '') { return }
  const delimiter = '-'
  const cnt = (value.match(new RegExp(delimiter, 'g')) || []).length
  if (cnt > 1) { return '郵便番号の形式が不正です' }

  const postcode = value.replace('-', '')
  if (postcode.length !== 7) { return '郵便番号の形式が不正です' }
  if (!core.isNumeric(postcode)) { return '郵便番号の形式が不正です' }

  return undefined
}

/**
 * 日付大小比較
 * @param {String} less
 * @param {String} bigger
 */
function compareDate (less, bigger) {
  const l = dayjs(less, 'YYYY/MM/DD', true).unix()
  const b = dayjs(bigger, 'YYYY/MM/DD', true).unix()

  return (l > b) ? '日付の関係性が不正です' : undefined
}

/**
 * 年月大小比較
 * @param {String} less
 * @param {String} bigger
 */
function compareYearMonth (less, bigger) {
  const l = dayjs(less + '/01', 'YYYY/MM/DD', true).unix()
  const b = dayjs(bigger + '/01', 'YYYY/MM/DD', true).unix()

  return (l > b) ? '年月の関係性が不正です' : undefined
}

/**
 * 時刻大小比較
 * @param {String} less
 * @param {String} bigger
 */
function compareTime (less, bigger) {
  const l = parseInt(less.split(':').join(''), 10)
  const b = parseInt(bigger.split(':').join(''), 10)

  return (l > b) ? '時刻の関係性が不正です' : undefined
}

/**
 * 日時大小比較
 * @param {String} lessYmd
 * @param {String} lessHms
 * @param {String} biggerYmd
 * @param {String} biggerHms
 */
function compareDateTime (lessYmd, lessHms, biggerYmd, biggerHms) {
  const l1s = dayjs(lessYmd, 'YYYY/MM/DD', true).unix().toString(10)
  const l2s = '0000' + parseInt(lessHms.split(':').join(''), 10).toString(10)
  const b1s = dayjs(biggerYmd, 'YYYY/MM/DD', true).unix().toString(10)
  const b2s = '0000' + parseInt(biggerHms.split(':').join(''), 10).toString(10)
  const l = parseInt(l1s + l2s.substr(-4), 10)
  const b = parseInt(b1s + b2s.substr(-4), 10)

  return (l > b) ? '日時の関係性が不正です' : undefined
}

/**
 * 数値大小比較
 * @param {Number} less
 * @param {Number} bigger
 */
function compareNumerical (less, bigger) {
  const l = less
  const b = bigger

  return (l > b) ? '大小関係性が不正です' : undefined
}

export default {
  paramStruct,
  requireValidate,
  requireMultiValidate,
  halfWidthKanaValidate,
  fullWidthKanaValidate,
  fullAngleValidate,
  halfAngleValidate,
  hiraganaValidate,
  numberValidate,
  alphanumericValidate,
  alphanumericWithSymbolValidate,
  numericalValidate,
  numericalDigitValidate,
  fixedNumericalDigitValidate,
  numericalRangeValidate,
  dateValidate,
  dateRangeValidate,
  yearMonthValidate,
  yearMonthRangeValidate,
  timeValidate,
  timeSpanValidate,
  lengthValidate,
  fixedLengthValidate,
  postCodeValidate,
  compareDate,
  compareYearMonth,
  compareTime,
  compareDateTime,
  compareNumerical
}
